import { memo } from "react";
import type { ReactElement } from "react";
import type { RecordAttachment } from "../utils/recordFiles";
import { fileNameWithExtension } from "../utils/recordFiles";

type FeedAttachmentsProps = {
  /** The message's `attachments` from Apex, already limited to the files this viewer may see (BR-3). */
  files?: RecordAttachment[] | null;
  /** The tab's own open function (openCaseAttachment / openChangeRequestAttachment). */
  onOpen: (file: RecordAttachment) => void;
};

/** The type label: the extension, uppercased. Empty when the file has none. */
function typeLabel(fileExtension: string | undefined): string {
  return typeof fileExtension === "string" ? fileExtension.trim().replace(/^\./, "").toUpperCase() : "";
}

/**
 * The files a staff member attached to one post or reply (ACOP-123 Amendment 3, BR-13, D23).
 *
 * Apex lists only the files this viewer may see and omits the key when there are none, so a missing
 * or empty list renders nothing and a message without files looks exactly as before. A click hands
 * the file to the tab's own open function, the one the Attachments list uses: the proxy, preview or
 * download, the 100 MB check and a single "isn't available" toast.
 *
 * Real <button>s, so Enter and Space open a file through the browser's own click; there is no
 * keydown handler, which would open it twice. Text only: copilot-ui ships a subsetted Font Awesome,
 * where a glyph class the subset lacks renders as nothing.
 */
function FeedAttachments({ files, onOpen }: FeedAttachmentsProps): ReactElement | null {
  if (!Array.isArray(files) || files.length === 0) return null;

  return (
    <ul className="list-unstyled d-flex flex-wrap gap-2 mt-2 mb-0" aria-label="Attached files" data-testid="feed-attachments">
      {files.map((file, index) => {
        const label = fileNameWithExtension(file.name || file.Name, file.fileExtension);
        const type = typeLabel(file.fileExtension);
        return (
          <li className="mw-100" style={{ minWidth: 0 }} key={file.docId || index}>
            <button
              type="button"
              className="btn btn-link btn-sm p-0 text-start text-decoration-none d-inline-flex align-items-center gap-1 mw-100"
              data-testid="feed-attachment-open"
              data-doc-id={file.docId || undefined}
              title={label}
              onClick={() => onOpen(file)}
            >
              <span className="text-truncate text-decoration-underline" style={{ minWidth: 0 }} data-testid="feed-attachment-name">
                {label}
              </span>
              {type && (
                <span className="badge rounded-pill bg-body-secondary text-body border fw-normal flex-shrink-0" data-testid="feed-attachment-type">
                  {type}
                </span>
              )}
            </button>
          </li>
        );
      })}
    </ul>
  );
}

export default memo(FeedAttachments);
